/**
 * Accessible confirmation dialog for destructive cart-drawer quantity changes.
 * The caller receives a Promise<boolean>; cart state is untouched until the customer confirms.
 */
if (!customElements.get('cart-remove-confirmation')) {
  customElements.define(
    'cart-remove-confirmation',
    class CartRemoveConfirmation extends HTMLElement {
      constructor() {
        super();

        this.dialog = this.querySelector('.cart-remove-confirmation__dialog');
        this.keepButton = this.querySelector('.cart-remove-confirmation__keep');
        this.fallbackAvatar = this.querySelector('[data-cart-remove-fallback-avatar]');
        this.closeTimer = undefined;
        this.resolveRequest = undefined;
        this.trigger = undefined;

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeyDown.bind(this));
        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') event.stopPropagation();
        });
      }

      request({ trigger, productImage } = {}) {
        if (this.resolveRequest) return Promise.resolve(false);

        window.clearTimeout(this.closeTimer);
        this.trigger = trigger instanceof HTMLElement ? trigger : undefined;
        this.updateFallbackAvatar(productImage);

        const drawerInner = this.closest('cart-drawer')?.querySelector('.drawer__inner');
        drawerInner?.setAttribute('inert', '');
        this.hidden = false;

        const response = new Promise((resolve) => {
          this.resolveRequest = resolve;
        });

        requestAnimationFrame(() => {
          this.classList.add('is-open');
          if (typeof trapFocus === 'function') {
            trapFocus(this.dialog, this.keepButton || this.dialog);
          } else {
            (this.keepButton || this.dialog)?.focus();
          }
        });

        return response;
      }

      updateFallbackAvatar(productImage) {
        if (!this.fallbackAvatar) return;

        const source = productImage?.currentSrc || productImage?.src;
        if (source) {
          this.fallbackAvatar.src = source;
          this.fallbackAvatar.hidden = false;
        } else {
          this.fallbackAvatar.removeAttribute('src');
          this.fallbackAvatar.hidden = true;
        }
      }

      onClick(event) {
        if (event.target.closest('[data-cart-remove-confirm]')) {
          this.finish(true);
          return;
        }

        if (event.target.closest('[data-cart-remove-dismiss]')) this.finish(false);
      }

      onKeyDown(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;

        event.preventDefault();
        event.stopPropagation();
        this.finish(false);
      }

      finish(confirmed) {
        if (!this.resolveRequest) return;

        const resolve = this.resolveRequest;
        const trigger = this.trigger;
        this.resolveRequest = undefined;
        this.trigger = undefined;

        this.classList.remove('is-open');
        this.closest('cart-drawer')?.querySelector('.drawer__inner')?.removeAttribute('inert');
        if (typeof removeTrapFocus === 'function') removeTrapFocus();

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.closeTimer = window.setTimeout(() => {
          this.hidden = true;

          if (!confirmed && trigger?.isConnected) {
            const cartDrawer = this.closest('cart-drawer')?.querySelector('#CartDrawer');
            if (cartDrawer && typeof trapFocus === 'function') {
              trapFocus(cartDrawer, trigger);
            } else {
              trigger.focus();
            }
          }
        }, reduceMotion ? 0 : 180);

        resolve(confirmed);
      }
    }
  );
}
